import React, { useContext, useState, useMemo } from 'react';

import { Segmented, notification } from 'antd';
import Axios from 'axios';

import styled from 'styled-components';

import { getContactGender, getContactMainFeatures, getMainFeatureValue } from '@libs/NwFeatures';
import { removeModelLog } from '@libs/NwLogs';
import { getAge, getNow, showShortDate } from '@libs/NwMoment';

import { useLayoutContext } from '@contexts/LayoutContext';
import { ListContext } from '@contexts/ListContext';
import { useStateValue } from '@contexts/StateProvider';
import { UserContext, useUserPermission } from '@contexts/UserContext';

import { useArchiveContact } from '@hooks/API/Contact/UseContact';
import { forceReloadModels, useModelsList } from '@hooks/API/Contact/UseContactsList';
import { useFeaturesIndex } from '@hooks/API/UseFeatures';

import ContactsExporter from '@components/ContactsExporter/ContactsExporter';
import AddToFolderButton from '@components/ContactsFolders/AddToFolderButton';
import {
    useContactsSelector,
    useContactsSelectorDispatch,
} from '@components/ContactsSelector/Common/ContactsSelectorContext';
import ContactUserForm from '@components/ContactUsersDrawer/ContactUserForm';
import { getPublicMainFeature } from '@components/Features/FeaturesGroupUtils';
import Switch from '@components/Gui/CustomSwitch';
import { NwButton, NwCancelButton } from '@components/Gui/NwButton';
import NwButtonBar from '@components/Gui/NwButtonBar';
import NwDrawer from '@components/Gui/NwDrawer';
import StyleVariables from '@components/Gui/StyleVariables.js';
import PermissionBlock from '@components/PermissionBlock/PermissionBlock';
import BulkTagPicker from '@components/TagPicker/BulkTagPicker';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const FixedFooter = styled.div`
    position: fixed;
    bottom: 0;
    left: ${(props) =>
        props.$isDrawer ? '' : props.$leftMargin ? props.$leftMargin : props.$size === 'small' ? '200px' : '300px'};
    background-color: #fff;
    padding: 16px 24px;
    box-sizing: border-box;
    z-index: 4;
    width: ${(props) =>
        props.$isDrawer
            ? ''
            : props.$leftMargin
              ? `calc(100vw - ${props.$leftMargin}px)`
              : props.size === 'small'
                ? 'calc(100vw - 200px)'
                : 'calc(100vw - 300px)'};
    border: ${StyleVariables.DefaultBorder};
    display: flex;
    justify-content: space-between;
    font-weight: bold;
    flex-wrap: wrap;
    text-transform: uppercase;
    align-items: center;
`;

const FooterItems = styled.div`
    display: flex;
    align-items: center;
`;

const SelectContainer = styled.div`
    margin-left: 16px;
`;

const FolderContainer = styled.div`
    font-weight: normal;
    text-transform: initial;
    margin-left: 8px;
`;

const TagsPopoverHeader = styled.div`
    text-align: center;

    .ant-segmented {
        margin: 0.5rem 0;
    }
`;

const ArchiveCopy = styled.div`
    p {
        margin: 0 0 1.5em 0;
        line-height: 1.5;
        font-weight: normal;
        text-transform: none;
    }

    p:first-of-type {
        font-weight: bold;
    }

    p:last-of-type {
        margin-bottom: 0;
    }
`;

// sezione del drawer di export: titolo + pulsante, separata da bordo inferiore
const ExportSection = styled.div`
    padding-bottom: 1.6rem;
    margin-bottom: 1.6rem;
    border-bottom: 1px solid #f0f0f0;

    p {
        margin: 0 0 1rem 0;
    }
`;

export const ModelsWallFooter = ({ models, onAddTag, onRemoveTag, isLoading }) => {
    const { breakpoints } = useLayoutContext();

    const { actions, additional, layout } = useContactsSelector();
    const { selectedItemsIDs, multipleSelectionActive } = actions;
    const { disabledItems } = additional;

    const cs_dispatch = useContactsSelectorDispatch();

    const featuresIndex = useFeaturesIndex();
    const [showContactUsers, setShowContactUsers] = useState(false);
    const [tagMode, setTagMode] = useState('Add');
    const [showDownloadDrawer, setShowDownloadDrawer] = useState(false);
    const [showTagsDrawer, setShowTagsDrawer] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [addressBookExportLoading, setAddressBookExportLoading] = useState(false);
    const [usePublicFeatures, setUsePublicFeatures] = useState(false);
    const [touchForm, setTouchForm] = useState(false);
    const tagsOnContacts = useUserPermission('TagsOnContacts', []);

    const ExportModelsList = useUserPermission('ExportModelsList');
    const canMassiveStorage = useUserPermission('ModelsMassiveStorage');
    const accessManagement = useUserPermission('AccessManagement');
    const canArchiveModels = canMassiveStorage || accessManagement;

    const { currentUser } = useContext(UserContext);
    const { cachedList } = useContext(ListContext);
    const [{ areaContext }] = useStateValue();
    const { mutateAsync: onArchiveContact } = useArchiveContact();
    const [showArchiveDrawer, setShowArchiveDrawer] = useState(false);
    const [archiveLoading, setArchiveLoading] = useState(false);

    const nwAgId = currentUser?.ActiveContext?.nwAgId;
    const { isSingleNetworkAgency, currentNetworkAgencyName } = useMemo(() => {
        const accountingGroups = cachedList?.accountingGroups?.items || [];
        let networkAgencyCount = 0;
        let agencyName = '';
        accountingGroups.forEach((group) => {
            (group.NetworkAgencies || []).forEach((agency) => {
                networkAgencyCount += 1;
                if (agency.ID === nwAgId) {
                    agencyName = agency.Name;
                }
            });
        });
        return {
            isSingleNetworkAgency: networkAgencyCount === 1,
            currentNetworkAgencyName: agencyName,
        };
    }, [cachedList, nwAgId]);

    // BulkTagPicker ricava da solo i tag applicabili o rimovibili: qui serve solo la selezione.
    // Si passa dall'hook e non da queryClient.getQueryData: leggere la cache dentro un useMemo
    // legato ai soli id non ricalcolerebbe dopo un reload, e i tag resterebbero quelli vecchi.
    const { data: allModels } = useModelsList();
    const selectedModels = useMemo(
        () => (allModels || []).filter((model) => selectedItemsIDs.includes(model.ID)),
        [allModels, selectedItemsIDs]
    );

    const handleSelectAll = () => {
        let newmodels = disabledItems
            ? models.filter((model) => !disabledItems.find((item) => item === model.ID))
            : models;
        const newSelectedList = newmodels.map((model) => model.ID);
        cs_dispatch({
            type: 'UpdateSelectedItems',
            newlist: [...newSelectedList],
        });
    };

    const handleAddUsers = () => {
        setShowContactUsers(true);
    };

    const handleCloseContacUsersDrawer = () => {
        setShowContactUsers(false);
    };

    const handleUpdateContactUsers = () => {
        forceReloadModels();
        setShowContactUsers(false);
    };

    const renderTagHeader = () => {
        return (
            <TagsPopoverHeader>
                <Segmented
                    value={tagMode}
                    onChange={setTagMode}
                    options={[
                        { label: 'Add Tags', value: 'Add' },
                        { label: 'Remove Tags', value: 'Remove' },
                    ]}
                />
            </TagsPopoverHeader>
        );
    };

    const handleOpenDownloadDrawer = () => {
        setShowDownloadDrawer(true);
    };

    const handleCloseDownloadDrawer = () => {
        setShowDownloadDrawer(false);
    };

    const handleExportModels = () => {
        if (!ExportModelsList || exportLoading) {
            return;
        }
        setExportLoading(true);
        Axios.post('/excel/models/export', { ContactIds: selectedItemsIDs }, { responseType: 'blob', timeout: 30000 })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.download = `models-${getNow().format('DDMMMYYYY')}.xlsx`;
                link.click();
                URL.revokeObjectURL(url);
                setExportLoading(false);
            })
            .catch((error) => {
                notification.error({
                    message: 'Error exporting models',
                    description: error.message,
                });
                setExportLoading(false);
            });
    };

    const handleExportAddressBook = () => {
        if (!ExportModelsList || addressBookExportLoading) {
            return;
        }
        setAddressBookExportLoading(true);
        Axios.post(
            '/Excel/Models/AddressBook/Export',
            { ContactIds: selectedItemsIDs },
            { responseType: 'blob', timeout: 30000 }
        )
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.download = `addressbook-${getNow().format('DDMMMYYYY')}.xlsx`;
                link.click();
                URL.revokeObjectURL(url);
                setAddressBookExportLoading(false);
            })
            .catch((error) => {
                notification.error({
                    message: 'Error exporting addressbook',
                    description: error.message,
                });
                setAddressBookExportLoading(false);
            });
    };

    const handleOpenArchiveDrawer = () => {
        setShowArchiveDrawer(true);
    };

    const handleCloseArchiveDrawer = () => {
        if (archiveLoading) {
            return;
        }
        setShowArchiveDrawer(false);
    };

    const handleConfirmArchive = async () => {
        if (archiveLoading || selectedItemsIDs.length === 0) {
            return;
        }
        setArchiveLoading(true);
        try {
            const results = await Promise.allSettled(
                selectedItemsIDs.map((contactId) =>
                    onArchiveContact({
                        contactId,
                        networkAgencyId: nwAgId,
                        contactType: 'Model',
                        area: areaContext.area,
                    })
                )
            );
            let failedCount = 0;
            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    removeModelLog(selectedItemsIDs[index], areaContext.area);
                } else {
                    failedCount += 1;
                    console.error('Store Contact Error => ', result.reason);
                }
            });
            if (failedCount === 0) {
                onClearSelection();
                setShowArchiveDrawer(false);
            } else {
                notification.error({
                    message: 'Error archiving models',
                    description:
                        failedCount === results.length
                            ? 'The selected models could not be archived.'
                            : `${failedCount} of ${results.length} models could not be archived.`,
                });
            }
        } catch (error) {
            console.error('Store Contact Error => ', error);
            notification.error({
                message: 'Error archiving models',
                description: error.message,
            });
        }
        setArchiveLoading(false);
    };

    const onClearSelection = () => {
        cs_dispatch({
            type: 'ClearSelectedItems',
        });
    };

    const handleFolderCreated = (folderid, foldername) => {
        cs_dispatch({
            type: 'SelectFolder',
            folderID: folderid.toString(),
            folderName: foldername,
        });
    };

    const getModelFeatures = (model) => {
        const gender = getContactGender(model);
        return getContactMainFeatures(model).map((mainFeature) => {
            // con l'opzione attiva la feature esce col valore pubblico, dove ce l'ha
            const publicMainFeature = usePublicFeatures ? getPublicMainFeature(mainFeature) : null;
            const featureToExport = publicMainFeature || mainFeature;
            return `${featureToExport.N} ${getMainFeatureValue(featureToExport, featuresIndex, { gender })}`;
        });
    };

    const selectedModelsData = useMemo(() => {
        const selectedModels = selectedItemsIDs.map((modelId) => {
            const model = models.find((model) => model.ID === modelId);
            if (model) {
                return {
                    ...model,
                    id: model.ID,
                    email: model.E,
                    name: model.N.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()),
                    surname: model.S.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase()),
                    age: getAge(model.B),
                    features: getModelFeatures(model).join(' • '),
                    tags: model.T.map((tag) => tag.Name).join(', '),
                    land: model.P,
                    mobile: model.M,
                    motherAgency: model.MAN,
                    birthdate: model.B ? showShortDate(model.B) : null,
                    nationality: model.NAT,
                };
            }
            return null;
        });
        return selectedModels;
    }, [models, selectedItemsIDs, usePublicFeatures, featuresIndex]);

    const selectedCount = selectedItemsIDs.length;
    const modelWord = selectedCount === 1 ? 'model' : 'models';
    const archiveIntro = isSingleNetworkAgency
        ? `You are about to archive ${selectedCount} ${modelWord}.`
        : `You are about to archive ${selectedCount} ${modelWord} for ${
              currentNetworkAgencyName || 'the current network agency'
          }.`;

    return (
        <FixedFooter
            $size={breakpoints < 4 ? 'small' : 'medium'}
            $leftMargin={layout.leftMargin}
            $isDrawer={layout.drawer}
        >
            <FooterItems>
                <div>
                    {selectedItemsIDs.length} Model{selectedItemsIDs.length > 1 && 's'} selected
                </div>
                &nbsp;
                {multipleSelectionActive && (
                    <SelectContainer>
                        <NwButton
                            ghost
                            icon={byPrefixAndName.fasl['check-double']}
                            label="select all"
                            onClick={handleSelectAll}
                        />
                        &nbsp;
                        <NwButton
                            ghost
                            icon={byPrefixAndName.fasl['times']}
                            label="clear selection"
                            onClick={onClearSelection}
                        />
                    </SelectContainer>
                )}
            </FooterItems>
            <FooterItems>
                <NwButton
                    disabled={selectedItemsIDs.length === 0}
                    ghost
                    label="export"
                    icon={byPrefixAndName.fasl['file-spreadsheet']}
                    onClick={handleOpenDownloadDrawer}
                />
                &nbsp;
                {selectedItemsIDs.length === 0 ? (
                    <NwButton
                        ghost
                        disabled={selectedItemsIDs.length === 0}
                        icon={byPrefixAndName.fasl['tag']}
                        label={'tags'}
                    />
                ) : tagsOnContacts ? (
                    <div onClick={() => setShowTagsDrawer(true)}>
                        <NwButton ghost icon={byPrefixAndName.fasl['tag']} label={'tags'} />
                    </div>
                ) : (
                    <PermissionBlock
                        permission={false}
                        content={<NwButton ghost icon={byPrefixAndName.fasl['tag']} label={'tags'} />}
                    />
                )}
                &nbsp;
                <NwButton
                    disabled={selectedItemsIDs.length === 0}
                    ghost
                    icon={byPrefixAndName.fasl['user-circle']}
                    label={'Booker'}
                    onClick={handleAddUsers}
                />
                &nbsp;
                <FolderContainer>
                    <AddToFolderButton
                        disabled={selectedItemsIDs.length === 0}
                        ids={selectedItemsIDs}
                        type="model"
                        placement="topLeft"
                        text="folders"
                        wide
                        onFolderCreated={handleFolderCreated}
                    />
                </FolderContainer>
                {canArchiveModels && (
                    <>
                        &nbsp;
                        <NwButton
                            disabled={selectedItemsIDs.length === 0}
                            ghost
                            danger
                            icon={byPrefixAndName.fasl['box-archive']}
                            label="archive"
                            onClick={handleOpenArchiveDrawer}
                        />
                    </>
                )}
                {showTagsDrawer && (
                    <NwDrawer
                        title={renderTagHeader()}
                        onClose={() => {
                            setShowTagsDrawer(false);
                        }}
                    >
                        <BulkTagPicker
                            itemType="Model"
                            mode={tagMode === 'Add' ? 'add' : 'remove'}
                            selectedItems={selectedModels}
                            onAddTag={onAddTag}
                            onRemoveTag={onRemoveTag}
                            outerLoading={isLoading}
                        />
                    </NwDrawer>
                )}
                {showArchiveDrawer && (
                    <NwDrawer title="Archive models" onClose={handleCloseArchiveDrawer}>
                        <ArchiveCopy>
                            <p>{archiveIntro}</p>
                            <p>Their details will remain available and can be restored later.</p>
                            <p>
                                Archiving removes each model from the website and blocks the external access used to
                                update their data.
                            </p>
                            <p>
                                If a model is restored, the website publication and the external access have to be
                                turned back on manually.
                            </p>
                        </ArchiveCopy>
                        <NwButtonBar align="center" margin="4rem 0 0 0">
                            <NwCancelButton disabled={archiveLoading} onClick={handleCloseArchiveDrawer} />
                            <NwButton
                                danger
                                primary
                                loading={archiveLoading}
                                loadingLabel="Archiving..."
                                label="confirm"
                                onClick={handleConfirmArchive}
                            />
                        </NwButtonBar>
                    </NwDrawer>
                )}
                {showContactUsers && (
                    <NwDrawer
                        touchForm={touchForm}
                        onTouchForm={setTouchForm}
                        title={'Add Contact User'}
                        onClose={handleCloseContacUsersDrawer}
                    >
                        <ContactUserForm
                            isModel
                            contactUserIds={selectedItemsIDs}
                            onCancel={handleCloseContacUsersDrawer}
                            onUpdated={handleUpdateContactUsers}
                            onTouchForm={setTouchForm}
                        />
                    </NwDrawer>
                )}
                {showDownloadDrawer && (
                    <NwDrawer title={'Export Models'} onClose={handleCloseDownloadDrawer}>
                        <ExportSection>
                            <p>
                                <strong>Export full dataset</strong>
                            </p>
                            <NwButton
                                disabled={!ExportModelsList}
                                loading={exportLoading}
                                primary
                                icon={byPrefixAndName.fasl['cloud-arrow-down']}
                                label="export full dataset"
                                onClick={handleExportModels}
                            />
                        </ExportSection>
                        <ExportSection>
                            <p>
                                <strong>Export addressbook</strong>
                            </p>
                            <NwButton
                                disabled={!ExportModelsList}
                                loading={addressBookExportLoading}
                                primary
                                icon={byPrefixAndName.fasl['cloud-arrow-down']}
                                label="export addressbook"
                                onClick={handleExportAddressBook}
                            />
                        </ExportSection>
                        <ContactsExporter
                            type="model"
                            headers={[
                                { label: 'Id', key: 'id' },
                                { label: 'Name', key: 'name' },
                                { label: 'Surname', key: 'surname' },
                                { label: 'E-mail address', key: 'email' },
                                { label: 'Mother Agency', key: 'motherAgency' },
                                { label: 'Age', key: 'age' },
                                { label: 'Birthdate', key: 'birthdate' },
                                { label: 'Nationality', key: 'nationality' },
                                { label: 'Features', key: 'features' },
                                { label: 'Tags', key: 'tags' },
                                { label: 'Land', key: 'land' },
                                { label: 'Mobile', key: 'mobile' },
                            ]}
                            headerExtras={{
                                features: (
                                    <Switch
                                        text="use public features"
                                        checked={usePublicFeatures}
                                        onChange={setUsePublicFeatures}
                                    />
                                ),
                            }}
                            data={selectedModelsData}
                        />
                    </NwDrawer>
                )}
            </FooterItems>
        </FixedFooter>
    );
};
